import { useState } from "react";
import { galleryImages } from "@/mocks/homeData";

export default function GallerySection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [startIndex, setStartIndex] = useState(0);

  const visibleCount = 3;
  const maxStart = Math.max(0, galleryImages.length - visibleCount);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () =>
    setLightboxIndex((prev) =>
      prev !== null ? (prev + 1) % galleryImages.length : null
    );
  const prevImage = () =>
    setLightboxIndex((prev) =>
      prev !== null
        ? (prev - 1 + galleryImages.length) % galleryImages.length
        : null
    );

  const goPrev = () => setStartIndex((prev) => Math.max(0, prev - 1));
  const goNext = () => setStartIndex((prev) => Math.min(maxStart, prev + 1));

  return (
    <>
      <section id="realisations" className="py-16 md:py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-sm font-semibold tracking-wider text-primary-500 uppercase mb-2 block">
              En images
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4 font-heading tracking-wide">
              Nos réalisations
            </h2>
            <p className="text-background-300 max-w-2xl mx-auto text-base md:text-lg">
              Découvrez l'atelier, l'accueil et les produits du garage Mobicar
              Pro au Tampon.
            </p>
          </div>

          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${startIndex * (100 / visibleCount)}%)` }}
              >
                {galleryImages.map((img, index) => (
                  <div
                    key={img.id}
                    className="w-1/3 shrink-0 px-2"
                  >
                    <button
                      type="button"
                      onClick={() => openLightbox(index)}
                      className="group relative block w-full aspect-square overflow-hidden rounded-lg bg-secondary-200 cursor-pointer text-left"
                      aria-label={`Ouvrir l'image : ${img.label}`}
                    >
                      <img
                        alt={img.alt}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        width="600"
                        height="600"
                        src={img.src}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={goPrev}
              disabled={startIndex === 0}
              className="absolute -left-3 md:-left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-background-300 flex items-center justify-center text-background-50 hover:bg-background-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-sm cursor-pointer"
              aria-label="Photos précédentes"
            >
              <i className="ri-arrow-left-s-line w-6 h-6 flex items-center justify-center text-2xl" />
            </button>

            <button
              type="button"
              onClick={goNext}
              disabled={startIndex === maxStart}
              className="absolute -right-3 md:-right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-background-300 flex items-center justify-center text-background-50 hover:bg-background-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-sm cursor-pointer"
              aria-label="Photos suivantes"
            >
              <i className="ri-arrow-right-s-line w-6 h-6 flex items-center justify-center text-2xl" />
            </button>
          </div>
        </div>
      </section>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
            aria-label="Image précédente"
          >
            <i className="ri-arrow-left-line w-8 h-8 flex items-center justify-center text-3xl" />
          </button>

          <div className="max-w-4xl max-h-[80vh] w-full flex flex-col items-center">
            <img
              alt={galleryImages[lightboxIndex].alt}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
              width="800"
              height="800"
              src={galleryImages[lightboxIndex].src}
            />
            <p className="text-white mt-4 text-sm font-medium">
              {galleryImages[lightboxIndex].label}
            </p>
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
            aria-label="Image suivante"
          >
            <i className="ri-arrow-right-line w-8 h-8 flex items-center justify-center text-3xl" />
          </button>

          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors p-2"
            aria-label="Fermer"
          >
            <i className="ri-close-line w-8 h-8 flex items-center justify-center text-3xl" />
          </button>
        </div>
      )}
    </>
  );
}